// Fallback labels for known redo reasons (used when org config is unavailable).
// Unknown reasons fall back to humanized snake_case in DefectBreakdownChart.
export const REDO_REASON_LABELS: Record<string, string> = {
  print_defect: 'Print Defect',
  press_issue: 'Press Issue',
  misprint: 'Misprint',
  stain_mark: 'Stain/Mark',
  wrong_size: 'Wrong Size',
  wrong_color: 'Wrong Color',
  damaged_garment: 'Damaged Garment',
  // Catcher-side wrong-item (Threadheads' configured value)
  wrong_item_fulfilled: 'Wrong Item (Fulfilled)',
  // Print-station garment-issue reasons (BLU-385)
  garment_contamination: 'Garment Contamination',
  manufacturer_defect: 'Manufacturer Defect',
  wrong_item_picked: 'Wrong Item (Picked)',
  other: 'Other',
};

// Redo reason is now a dynamic string (per-org configurable)
export type RedoReason = string;

export interface DefectTrendPoint {
  period: string;
  totalProcessed: number;
  defectCount: number;
  defectRate: number;
}

export interface DefectBreakdown {
  reason: RedoReason;
  count: number;
  percentage: number;
}

export interface InventoryErrorItem {
  id: string;
  createdAt: string;
  issueType: 'OOS_PRE_BATCH' | 'INVENTORY_ERROR' | 'PAP_DAMAGED';
  blockedSku: string | null;
  blockedQuantity: number | null;
  orderName: string | null;
  status: 'OPEN' | 'RESOLVED' | 'ROUTED' | 'CANCELLED';
  /** Routed-ness is keyed off resolution_method='routed' (status='ROUTED' is legacy). */
  resolutionMethod?: string | null;
}

/**
 * Display status for an inventory-error row. Routing resolves issues as
 * RESOLVED + resolution_method='routed' (legacy rows carry status='ROUTED'),
 * so the "Routed" distinction is derived here — used by BOTH the badge and
 * the CSV export so screen and export never disagree.
 *
 * KEEP IN LOCKSTEP with the server-side export derivation in
 * apps/backend/src/modules/qa-reports/qa-reports.handler.ts (export mapping) —
 * the same routed-ness rule is applied on both sides of the tRPC boundary.
 */
export function getInventoryErrorDisplayStatus(
  error: Pick<InventoryErrorItem, 'status' | 'resolutionMethod'>
): InventoryErrorItem['status'] {
  if (error.status === 'ROUTED') return 'ROUTED';
  if (error.status === 'RESOLVED' && error.resolutionMethod === 'routed') return 'ROUTED';
  return error.status;
}

// Human-readable labels for issue types
export const ISSUE_TYPE_LABELS: Record<InventoryErrorItem['issueType'], string> = {
  OOS_PRE_BATCH: 'Out of Stock',
  INVENTORY_ERROR: 'Inventory Error',
  PAP_DAMAGED: 'Damaged (P&P)',
};

export interface SummaryStats {
  totalDefects: number;
  totalProcessed: number;
  openIssues: number;
  totalOrders: number;
  printFileIssues: number;
}

export interface PrintFileBreakdown {
  reason: string;
  count: number;
  percentage: number;
}

export interface QAReportStats {
  defectTrend: DefectTrendPoint[];
  defectBreakdown: DefectBreakdown[];
  inventoryErrors: InventoryErrorItem[];
  printFileBreakdown: PrintFileBreakdown[];
  summaryStats: SummaryStats;
}

export type Granularity = 'day' | 'week' | 'month';

export interface DateRange {
  from: Date;
  to: Date;
}

// Date range preset with consistent function-based approach
export interface DatePreset {
  label: string;
  getValue: () => DateRange;
}
